import { useEffect, useRef } from "react";
import * as THREE from "three";

export default function Hero3D() {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    /* ── Renderer ── */
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: "high-performance",
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.8));
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);

    /* ── Scene / Camera ── */
    const scene = new THREE.Scene();
    scene.fog = new THREE.Fog(0x070605, 6, 18);

    const camera = new THREE.PerspectiveCamera(45, mount.clientWidth / mount.clientHeight, 0.1, 100);
    camera.position.set(0, 0, 7);


    /* ── Rig (mouse parallax) ── */
    const rig = new THREE.Group();
    scene.add(rig);



    /* ── Dust particles ── */
    const PARTICLE_COUNT = 380;
    const positions = new Float32Array(PARTICLE_COUNT * 3);
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      positions[i * 3]     = (Math.random() - 0.5) * 18;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 12;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 10 - 2;
    }
    const dustGeo = new THREE.BufferGeometry();
    dustGeo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    const dustMat = new THREE.PointsMaterial({
      size: 0.035,
      color: 0xe8c874,
      transparent: true,
      opacity: 0.55,
      sizeAttenuation: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    const dust = new THREE.Points(dustGeo, dustMat);
    rig.add(dust);

    /* ── Mouse parallax ── */
    let pointer = { x: 0, y: 0 };
    const onMouseMove = (e) => {
      pointer.x = (e.clientX / window.innerWidth)  * 2 - 1;
      pointer.y = (e.clientY / window.innerHeight) * 2 - 1;
    };
    window.addEventListener("mousemove", onMouseMove);

    /* ── Resize ── */
    const onResize = () => {
      camera.aspect = mount.clientWidth / mount.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(mount.clientWidth, mount.clientHeight);
    };
    window.addEventListener("resize", onResize);

    /* ── Animation loop ── */
    let animId;
    const clock = new THREE.Clock();
    const rigRotTarget = { x: 0, y: 0 };
    const rigPosTarget = { x: 0 };

    const animate = () => {
      animId = requestAnimationFrame(animate);
      const elapsed = clock.getElapsedTime();
      const delta = clock.getDelta ? 0.016 : 0.016;

      /* dust drift */
      dust.rotation.y = elapsed * 0.02;
      dust.position.y = Math.sin(elapsed * 0.2) * 0.3;

      /* smooth mouse parallax on rig */
      rigRotTarget.y = pointer.x * 0.35;
      rigRotTarget.x = -pointer.y * 0.25;
      rigPosTarget.x = pointer.x * 0.6;
      rig.rotation.y += (rigRotTarget.y - rig.rotation.y) * 0.05;
      rig.rotation.x += (rigRotTarget.x - rig.rotation.x) * 0.05;
      rig.position.x += (rigPosTarget.x - rig.position.x) * 0.04;

      renderer.render(scene, camera);
    };
    animate();

    /* ── Cleanup ── */
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("resize", onResize);
      renderer.dispose();
      if (mount.contains(renderer.domElement)) {
        mount.removeChild(renderer.domElement);
      }
    };
  }, []);

  return (
    <div
      ref={mountRef}
      style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
      aria-hidden="true"
    />
  );
}
